import { useCallback, useMemo, useRef, useState } from "react";
import { useStore } from "react-redux";
import { cloneDeep, identity, isEqual, pick } from "lodash";

/**
 * Keeps "Save Changes" disabled until the form differs from its hydrated baseline.
 * The shared form reports its hydrated payload via `onHydrate`; only those keys are
 * compared, since the Redux slice also contains create-flow defaults. Until hydration
 * completes, the section counts as changed so the button remains usable.
 */
export const useSectionChanges = (current, normalize = identity) => {
  const [baseline, setBaseline] = useState(null);

  // Latest hydration wins: refetched values replace the baseline,
  // so unchanged forms aren't incorrectly marked as modified.
  const onHydrate = useCallback((values) => {
    setBaseline((existing) => (isEqual(existing, values) ? existing : cloneDeep(values)));
  }, []);

  const hasChanges = useMemo(() => {
    if (!baseline) return true;
    return !isEqual(normalize(pick(current, Object.keys(baseline))), normalize(baseline));
  }, [current, baseline, normalize]);

  return { hasChanges, onHydrate };
};

/**
 * For sections without hydration (Location, Qualifications), snapshot the
 * prefilled slice on first render and use it as the change baseline.
 *
 * `normalize` strips anything volatile from both sides before comparing — the
 * qualification rows carry a `Date.now()` id and a blank placeholder row, so a
 * plain deep-equal reports a change on every re-hydration.
 */
export const useStoreSectionChanges = (selectSection, current, normalize = identity) => {
  const store = useStore();
  const baselineRef = useRef(null);

  if (baselineRef.current === null) {
    baselineRef.current = cloneDeep(selectSection(store.getState()));
  }

  return useMemo(
    () => !isEqual(normalize(current), normalize(baselineRef.current)),
    [current, normalize],
  );
};
